import z from 'zod';
import type { Contact } from './contacts';
import {
    type DraftFormResponseField,
    type ProspectFormResponseField,
    getDraftFieldValue,
    getProspectFieldValue,
    parseDraftFormResponse,
    parseProspectFormResponse,
} from './formResponseParsing';
import type { QuestionSchema } from './templateFormFields';

export type { DraftFormResponseField, ProspectFormResponseField };
export {
    getDraftFieldValue,
    getProspectFieldValue,
    parseDraftFormResponse,
    parseProspectFormResponse,
};

export const GetDraftFormSubmissionBodySchema = z.object({
    clientEmail: z.string().toLowerCase(),
    draftFormSubmissionId: z.guid(),
});
export type GetDraftFormSubmissionBody = z.infer<typeof GetDraftFormSubmissionBodySchema>;

export const GetFormSubmissionBodySchema = z.object({
    formSubmissionId: z.guid(),
    clientEmail: z.string().toLowerCase(),
    prospectVersionId: z.union([z.number(), z.string().transform(Number)]),
});
export type GetFormSubmissionBody = z.infer<typeof GetFormSubmissionBodySchema>;

export const ContactRequestBodySchema = z.object({
    prospectId: z.guid().optional(),
    contactEmail: z.string().toLowerCase(),
    prospectVersionId: z
        .union([z.string().transform(Number), z.number()])
        .nullable()
        .optional(),
});
export type ContactRequestBody = z.infer<typeof ContactRequestBodySchema>;

/**
 * Contact form values for Intake Form V2 forms.
 * Stored separately from templateFormFields in draft and submitted form snapshots.
 *
 * Note: The frontend UI state type extends this to use DateTime instead of string for dateOfBirth.
 */
export const ContactFormValuesSchema = z.object({
    // The party (contacts row) these values belong to. Missing on entries saved before entries
    // carried it, and on parties the matter doesn't have yet (pairEntriesWithContacts)
    contactId: z.guid().optional(),
    // ID document fields (file IDs stored in blob storage)
    firstIdType: z.string().optional(),
    secondIdType: z.string().optional(),
    firstIdFront: z.string().optional(),
    firstIdBack: z.string().optional(),
    firstIdSingle: z.string().optional(),
    secondIdFront: z.string().optional(),
    secondIdBack: z.string().optional(),
    secondIdSingle: z.string().optional(),
    // Required contact fields
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    cellPhone: z.string().optional(),
    // Optional contact fields
    middleName: z.string().optional(),
    homePhone: z.string().optional(),
    homeAddress: z.string().optional(),
    homeUnit: z.string().optional(),
    dateOfBirth: z.string().nullable().optional(),
    gender: z.string().optional(),
    preferredPronouns: z.string().optional(),
    maritalStatus: z.string().optional(),
    citizenshipStatus: z.string().optional(),
    occupation: z.string().optional(),
    employer: z.string().optional(),
    employerAddress: z.string().optional(),
    employerUnit: z.string().optional(),
    employerPhone: z.string().optional(),
});
export type ContactFormValues = z.infer<typeof ContactFormValuesSchema>;

export interface IntakeSummaryPdfFormSubmissionField {
    templateFormFieldId?: string | null;
    submissionResponse?: string | null;
    name?: string | null;
    title?: string | null;
    questionSchema?: QuestionSchema | null;
    formGroupTitle?: string | null;
    displayOrder?: number | null;
    isCriticalQuestion?: boolean | null;
}

export interface IntakeSummaryPdfData {
    matterType?: string | null;
    formSubmissionFields: IntakeSummaryPdfFormSubmissionField[];
    contactFormValues: ContactFormValues[];
    showEmptyAnswersInIntakeSummary?: boolean;
    showContactsAsColumnsInIntakeSummary?: boolean;
}

export const DraftFormSubmissionResponseSchema = z.object({
    draftFormSubmissionId: z.guid(),
    templateFormId: z.guid(),
    prospectId: z.guid().nullable().optional(),
    clientEmail: z.string(),
    /**
     * JSON-stringified array of `DraftFormResponseField[]`.
     * Use `parseDraftFormResponse(formResponse)` to get typed access.
     */
    formResponse: z.string(),
    /**
     * Contact form values stored separately from templateFormFields.
     * When loading draft data, apply these first, then overlay with contactResponses.
     */
    contactFormValues: z.array(ContactFormValuesSchema).nullable().optional(),
    createdAt: z.date(),
});

/**
 * Response from fetching a draft form submission.
 *
 * Note: `formResponse` is a JSON string. Use `parseDraftFormResponse()` to parse it:
 * @example
 * const response = await verifyClientForDraftForm(payload);
 * const fields = parseDraftFormResponse(response.formResponse);
 */
export type DraftFormSubmissionResponse = z.infer<typeof DraftFormSubmissionResponseSchema> & {
    contactId?: string;
    contactResponses?: Contact[];
};

export interface FormSubmissionResponse {
    prospectId: string;
    formSubmissionId: string;
    templateFormId: string;
    formSubmissionFields: { formFieldId: string; formFieldResponse: string | null }[];
    submittedBy: string | null;
    submittedAt: Date;
    contactResponses: Contact[];
    contactFormValues?: ContactFormValues[] | null;
}
